import axios from 'axios';
import { Sentry } from '../../Utils/sentry';
import { checkCredentials } from '../../Contexts/AuthContext';
class AxiosWrapper {
  constructor(baseURL, username, password, setLoading) {
    this.baseURL = baseURL;
    this.username = username;
    this.password = password;
    this.setLoading = setLoading || (() => {});
    this.axiosInstance = this.createInstance();
  }

  createInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 180000,
    });

    instance.interceptors.request.use(
      (config) => {
        if (this.setLoading) this.setLoading(true);

        console.log('AxiosWrapper interceptor - URL:', config.url);
        console.log('AxiosWrapper interceptor - isAuthToken:', config.headers.isAuthToken);
        console.log('AxiosWrapper interceptor - existing token in headers:', config.headers.token);

        // Only automatically add auth token if it's not explicitly set to false
        // and if no token is already present in headers
        const authToken = this.getAuthToken();
        if (authToken && !config.headers.token && config.headers.isAuthToken !== false) {
          console.log('AxiosWrapper interceptor - Adding auth token to headers');
          config.headers.token = authToken;
          config.headers.Authorization = `Bearer ${authToken}`;
        } else {
          console.log('AxiosWrapper interceptor - NOT adding auth token:', {
            hasAuthToken: !!authToken,
            hasExistingToken: !!config.headers.token,
            isAuthTokenFlag: config.headers.isAuthToken,
          });
        }

        // Remove the isAuthToken flag from headers as it's just for our logic
        delete config.headers.isAuthToken;

        return config;
      },
      (error) => {
        if (this.setLoading) this.setLoading(false);
        return Promise.reject(error instanceof Error ? error : new Error(error));
      },
    );

    instance.interceptors.response.use(
      (response) => {
        if (this.setLoading) this.setLoading(false);
        return response;
      },
      (error) => {
        if (this.setLoading) this.setLoading(false);
        if (error.response) {
          Sentry.captureException(new Error(`API Error: ${error.response.status} ${error.response.statusText}`));
        } else if (error.request) {
          Sentry.captureException(new Error('API Error: No response received'));
        } else {
          Sentry.captureException(error);
        }
        return Promise.reject(error instanceof Error ? error : new Error(error));
      },
    );

    return instance;
  }

  setAuthToken(token) {
    localStorage.setItem('token', token);
  }

  getAuthToken() {
    const token = localStorage.getItem('token');
    console.log('AxiosWrapper.getAuthToken() returning:', token ? `${token.substring(0, 20)}...` : 'null');
    return token;
  }

  validateToken() {
    return new Promise((resolve, reject) => {
      const token = this.getAuthToken();
      if (!token) {
        resolve(false);
      } else {
        checkCredentials()
          .then((response) => {
            if (!response) {
              this.deauthenticate();
              resolve(false);
            } else {
              resolve(response);
            }
          })
          .catch((error) => {
            console.error(error);
            reject(error instanceof Error ? error : new Error(error));
          });
      }
    });
  }

  removeAuthToken() {
    localStorage.removeItem('token');
  }

  authenticate(token) {
    this.setAuthToken(token);
  }

  deauthenticate() {
    this.removeAuthToken();
  }

  get(url, params = {}) {
    return this.axiosInstance.get(url, { params });
  }

  post(url, data = {}) {
    return this.axiosInstance.post(url, data);
  }

  put(url, data = {}) {
    return this.axiosInstance.put(url, data);
  }

  delete(url, params = {}) {
    return this.axiosInstance.delete(url, { params });
  }
}

export default AxiosWrapper;
